// Property create / edit wizard.
//
// Top-level orchestrator that:
//   - Owns the {@link PropertyDraft} working state.
//   - Renders the top bar (back + breadcrumb + draft + step counter).
//   - Renders the {@link StepProgress} rail.
//   - Renders the active step (from `wizard/steps/*`).
//   - Renders the desktop-only {@link PreviewPanel}.
//   - Renders the footer "Back / Continue / Publish" buttons.
//
// Submit responsibilities live with the consuming page — create and
// edit have different mutations. The wizard surfaces `onSubmit(draft)`
// + a `submitting` flag and otherwise stays pure UI.

import { useEffect, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "react-router";
import { ArrowLeft, ArrowRight, Check, ChevronLeft, ChevronRight } from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { BasicInfoStep } from "@/features/property/wizard/steps/basic-info-step";
import { DetailsStep } from "@/features/property/wizard/steps/details-step";
import { ImagesStep } from "@/features/property/wizard/steps/images-step";
import type { PendingImage } from "@/features/property/wizard/steps/pending-image-buffer";
import { PublishStep } from "@/features/property/wizard/steps/publish-step";
import { PreviewPanel } from "@/features/property/wizard/preview-panel";
import { StepProgress } from "@/features/property/wizard/step-progress";
import { useSwipeNav } from "@/features/property/wizard/use-swipe-nav";
import {
  EMPTY_DRAFT,
  WIZARD_STEPS,
  nextStep,
  prevStep,
  stepIndex,
  validateStep,
  type PropertyDraft,
  type WizardStep,
} from "@/features/property/wizard/wizard-state";

export interface PropertyWizardSubmitParams {
  draft: PropertyDraft;
  /**
   * Files the user buffered during step 3 of the create flow. Empty in
   * edit mode (where the live ImageUploader handles uploads directly).
   * The consuming page is responsible for posting them after the
   * property is created, in the order they appear here.
   */
  pendingImages: readonly PendingImage[];
}

export interface PropertyWizardProps {
  /** "create" buffers images locally; "edit" mounts the live uploader. */
  mode: "create" | "edit";
  /** Pre-populated draft (edit mode); leave undefined for create. */
  initialDraft?: PropertyDraft | undefined;
  /** Set in edit mode so the wizard knows the persisted id. */
  propertyId?: number | undefined;
  /**
   * Edit mode: URL of the property's current thumbnail, shown as the
   * preview-panel hero until the user buffers a new first image. Create
   * mode leaves it undefined (the buffered image drives the preview).
   */
  initialHeroImageUrl?: string | undefined;
  /** Page title shown in the top bar. Defaults match the design comp. */
  title?: string | undefined;
  /** Called when the user clicks the final action (publish or save). */
  onSubmit: (params: PropertyWizardSubmitParams) => Promise<void> | void;
  /** Disable the action button + show a loading state. */
  submitting?: boolean | undefined;
}

export function PropertyWizard({
  mode,
  initialDraft,
  propertyId,
  initialHeroImageUrl,
  title,
  onSubmit,
  submitting = false,
}: PropertyWizardProps) {
  const navigate = useNavigate();
  const [draft, setDraft] = useState<PropertyDraft>(initialDraft ?? EMPTY_DRAFT);
  const [step, setStep] = useState<WizardStep>("basic");
  const [showValidation, setShowValidation] = useState(false);
  // Buffered files used by the create-flow ImagesStep. Empty + unused
  // in edit mode (where the live ImageUploader manages uploads itself).
  // The wizard owns the object-URL lifecycle so navigating between
  // steps (which unmounts/remounts PendingImageBuffer) doesn't kill
  // the previews. A ref tracks the latest list — updated in a no-dep
  // useEffect so we never mutate during render — and the unmount
  // cleanup reads it to revoke whatever's still around.
  const [pendingImages, setPendingImages] = useState<PendingImage[]>([]);
  const pendingImagesRef = useRef<readonly PendingImage[]>([]);
  useEffect(() => {
    pendingImagesRef.current = pendingImages;
  });
  useEffect(() => {
    return () => {
      for (const image of pendingImagesRef.current) {
        URL.revokeObjectURL(image.previewUrl);
      }
    };
  }, []);

  const validation = validateStep(step, draft);
  const currentIdx = stepIndex(step);
  const totalSteps = WIZARD_STEPS.length;
  const isLast = step === "publish";
  const isFirst = step === "basic";

  const applyChanges = (changes: Partial<PropertyDraft>) => {
    setDraft((prev) => ({ ...prev, ...changes }));
    // Live-clear validation messages as the user starts fixing things.
    if (showValidation) setShowValidation(false);
  };

  const handleContinue = () => {
    // Free forward navigation — matches the step rail behavior. The
    // final "Xuất bản" still validates the whole draft and jumps the
    // user back to the first failing step if anything's missing.
    const target = nextStep(step);
    if (target) {
      setStep(target);
      setShowValidation(false);
    }
  };

  const handleBack = () => {
    const target = prevStep(step);
    if (target) {
      setStep(target);
      setShowValidation(false);
    }
  };

  const handleSubmit = async () => {
    // Full-draft validation only happens at submit time. If something
    // is missing, jump back to the first failing step + reveal inline
    // errors so the user knows where to go.
    const firstInvalid = WIZARD_STEPS.find(
      (s) => !validateStep(s.key, draft).ok,
    );
    if (firstInvalid !== undefined) {
      setStep(firstInvalid.key);
      setShowValidation(true);
      return;
    }
    await onSubmit({ draft, pendingImages });
  };

  const handleStepJump = (target: WizardStep) => {
    // Free navigation in both directions — the step rail is a quick
    // way to bounce around the wizard. Required-field validation still
    // fires on "Tiếp tục" + the final "Xuất bản", which is where it
    // actually gates progress.
    setStep(target);
    setShowValidation(false);
  };

  const effectiveValidation = showValidation
    ? validation
    : { ok: validation.ok, errors: {} as typeof validation.errors };

  // First buffered image's preview is the preview-pane hero — matches the
  // design promise that the very first image becomes the listing's
  // thumbnail. In edit mode there's no buffer, so fall back to the
  // property's current thumbnail; failing both, the preview shows its
  // gradient placeholder.
  const previewHeroUrl = pendingImages[0]?.previewUrl ?? initialHeroImageUrl;

  // Mobile users get horizontal swipe navigation between steps in
  // addition to tapping the progress bars. Left swipe → next step,
  // right swipe → previous. Vertical-dominant gestures are ignored
  // so the form column still scrolls normally (see useSwipeNav).
  const swipeHandlers = useSwipeNav({
    onSwipeLeft: () => {
      const target = nextStep(step);
      if (target) handleStepJump(target);
    },
    onSwipeRight: () => {
      const target = prevStep(step);
      if (target) handleStepJump(target);
    },
  });

  return (
    <div className="flex h-full min-h-svh flex-col">
      <TopBar
        title={title ?? (mode === "create" ? "Thêm bất động sản mới" : "Chỉnh sửa BĐS")}
        stepNumber={currentIdx + 1}
        totalSteps={totalSteps}
        onBack={() => navigate(-1)}
      />
      <StepProgress current={step} onJump={handleStepJump} />

      <div className="flex flex-1 overflow-hidden">
        <div className="flex flex-1 flex-col overflow-y-auto">
          <div
            className="flex-1 touch-pan-y px-6 py-6 md:px-9 md:py-7"
            {...swipeHandlers}
          >
            <StepBody
              step={step}
              mode={mode}
              draft={draft}
              propertyId={propertyId}
              onChange={applyChanges}
              validation={effectiveValidation}
              pendingImages={pendingImages}
              onPendingImagesChange={setPendingImages}
            />
          </div>
          <FooterNav
            isFirst={isFirst}
            isLast={isLast}
            submitting={submitting}
            onBack={handleBack}
            onContinue={handleContinue}
            onSubmit={() => void handleSubmit()}
          />
        </div>
        <PreviewPanel draft={draft} heroImageUrl={previewHeroUrl} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Top bar
// ---------------------------------------------------------------------------

function TopBar({
  title,
  stepNumber,
  totalSteps,
  onBack,
}: {
  title: string;
  stepNumber: number;
  totalSteps: number;
  onBack: () => void;
}) {
  return (
    <header className="flex items-center gap-3 border-b border-border bg-card px-4 py-3 md:px-8 md:py-4">
      <button
        type="button"
        onClick={onBack}
        aria-label="Quay lại"
        className="inline-flex size-8 items-center justify-center rounded-lg border border-border bg-card hover:bg-muted"
      >
        <ChevronLeft aria-hidden="true" className="size-3.5" />
      </button>

      <nav
        aria-label="Đường dẫn"
        className="hidden items-center gap-2 text-[14px] md:flex"
      >
        <span className="text-muted-foreground">BĐS</span>
        <ChevronRight aria-hidden="true" className="size-3 text-ink-faint" />
        <span className="font-semibold">{title}</span>
      </nav>

      <h1 className="text-[16px] font-semibold md:hidden">{title}</h1>

      <span className="ml-auto font-mono text-[12px] text-muted-foreground">
        Bước {stepNumber} / {totalSteps}
      </span>
    </header>
  );
}

// ---------------------------------------------------------------------------
// Step dispatch
// ---------------------------------------------------------------------------

function StepBody({
  step,
  mode,
  draft,
  propertyId,
  onChange,
  validation,
  pendingImages,
  onPendingImagesChange,
}: {
  step: WizardStep;
  mode: "create" | "edit";
  draft: PropertyDraft;
  propertyId: number | undefined;
  onChange: (changes: Partial<PropertyDraft>) => void;
  validation: ReturnType<typeof validateStep>;
  pendingImages: readonly PendingImage[];
  onPendingImagesChange: (next: PendingImage[]) => void;
}): ReactNode {
  switch (step) {
    case "basic":
      return (
        <BasicInfoStep
          draft={draft}
          onChange={onChange}
          validation={validation}
          mode={mode}
        />
      );
    case "details":
      return (
        <DetailsStep draft={draft} onChange={onChange} validation={validation} />
      );
    case "images":
      return (
        <ImagesStep
          propertyId={propertyId}
          pendingImages={pendingImages}
          onPendingImagesChange={onPendingImagesChange}
        />
      );
    case "publish":
      return (
        <PublishStep
          draft={draft}
          onChange={onChange}
          propertyId={propertyId}
        />
      );
  }
}

// ---------------------------------------------------------------------------
// Footer (sticky on mobile, inline on desktop)
// ---------------------------------------------------------------------------

function FooterNav({
  isFirst,
  isLast,
  submitting,
  onBack,
  onContinue,
  onSubmit,
}: {
  isFirst: boolean;
  isLast: boolean;
  submitting: boolean;
  onBack: () => void;
  onContinue: () => void;
  onSubmit: () => void;
}) {
  return (
    <footer
      className={cn(
        "sticky bottom-0 flex items-center gap-3 border-t border-border bg-card px-4 py-3",
        "md:static md:px-9 md:py-4",
      )}
    >
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={onBack}
        disabled={isFirst}
      >
        <ArrowLeft aria-hidden="true" className="size-3.5" />
        <span>Quay lại</span>
      </Button>
      <div className="flex-1" />
      {isLast ? (
        <Button
          type="button"
          size="sm"
          onClick={onSubmit}
          disabled={submitting}
          className="bg-success text-background hover:bg-success/90"
        >
          <Check aria-hidden="true" className="size-3.5" />
          {submitting ? "Đang xuất bản…" : "Xuất bản BĐS"}
        </Button>
      ) : (
        <Button
          type="button"
          variant="primary"
          size="sm"
          onClick={onContinue}
        >
          <span>Tiếp tục</span>
          <ArrowRight aria-hidden="true" className="size-3.5" />
        </Button>
      )}
    </footer>
  );
}
